// src/ryan/admin/components/tabs/RyanAdminAnalyticsTimeline.tsx
//
// Fase 4 do analytics de funil — modo "Timeline" dentro de RyanAdminAnalytics.tsx.
// Duas telas dentro do mesmo componente: lista de sessões (pra escolher qual
// investigar) e a timeline de uma sessão selecionada (eventos crus em ordem
// cronológica, traduzidos pra frase legível aqui no frontend — o backend só
// devolve eventType+metadata, mesma separação dado/apresentação de /funnel
// e /behavior).
//
// "Sessão" não é um documento — é inferida no backend agrupando eventos por
// sessionId. outcome (convertida/abandonou checkout/só navegou) também é
// derivado, nunca gravado.

import React, { useEffect, useState } from 'react';
import { formatDuration } from './RyanAdminAnalytics';

const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string, opts?: RequestInit) =>
    fetch(`/api/ryan${path}`, { ...opts, headers: { Authorization: `Bearer ${tok()}`, ...(opts?.headers || {}) } });

interface SessionSummary {
    sessionId: string;
    visitorId: string;
    userLabel: string | null;
    source: string;
    firstEventAt: string;
    lastEventAt: string;
    durationSeconds: number;
    eventCount: number;
    furthestStep: { key: string; label: string };
    outcome: 'converted' | 'abandoned_checkout' | 'browsed_only';
}

interface SessionsResponse {
    sessions: SessionSummary[];
    total: number;
    page: number;
    pageSize: number;
}

interface TimelineEvent {
    timestamp: string;
    page: string;
    eventType: string;
    metadata: Record<string, any>;
}

interface TimelineResponse {
    sessionId: string;
    visitorId: string;
    source: string;
    userLabel: string | null;
    events: TimelineEvent[];
}

// Nível 1: sem venda envolvida — exclusão completa e segura.
// Nível 2: tentativa de compra que nunca virou venda — exclusão inclui essa
//          request órfã (e libera a vaga do cupom, se reservada).
// Nível 3: virou venda aprovada — fora de escopo aqui, de propósito; só os
//          eventos de comportamento podem ser apagados (ver DeleteModal).
interface DeletePreview {
    tier: 1 | 2 | 3;
    eventCount: number;
    firstEventAt: string;
    lastEventAt: string;
    source: string;
    accessRequest: {
        _id: string;
        status: string;
        email: string;
        createdAt: string;
        approvedAt: string | null;
        couponReserved: boolean;
        couponCode: string | null;
    } | null;
}

const REQUEST_STATUS_LABELS: Record<string, string> = {
    pending: 'pendente — nunca chegou a pagar',
    awaiting_payment: 'aguardando confirmação de pagamento',
    rejected: 'rejeitada manualmente',
    payment_failed: 'pagamento falhou/foi recusado',
    approved: 'aprovada',
};

interface Props {
    productId: string;
    // { period: '30d' } nos presets, ou { period: 'custom', from, to } no
    // intervalo customizado — montado uma única vez no componente pai
    // (RyanAdminAnalytics.tsx) pra não duplicar a lógica de from/to aqui.
    // null enquanto um intervalo customizado inválido (from > to, ou algum
    // dos dois vazio) — nesse estado, não busca nada até ficar válido.
    periodParams: Record<string, string> | null;
    source: string;
}

const OUTCOMES: { value: string; label: string }[] = [
    { value: 'all', label: 'Todas' },
    { value: 'converted', label: 'Converteram' },
    { value: 'abandoned_checkout', label: 'Abandonaram checkout' },
    { value: 'browsed_only', label: 'Só navegaram' },
];

const OUTCOME_LABELS: Record<string, string> = {
    converted: 'Converteu',
    abandoned_checkout: 'Abandonou checkout',
    browsed_only: 'Só navegou',
};

function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatTimelineTime(iso: string): string {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// Traduz eventType+metadata pra uma frase — único lugar que conhece o
// vocabulário de cada tipo de evento na apresentação (o backend só repassa
// os dados crus gravados desde a Fase 1).
function describeEvent(e: TimelineEvent): string {
    const m = e.metadata || {};
    switch (e.eventType) {
        case 'pageview': return 'Entrou na página do produto';
        case 'scroll_depth': return `Rolou até ${m.percent}% da página`;
        case 'click_preview': return m.source === 'hero' ? 'Clicou em "Acessar prévias"' : 'Abriu a prévia de um item';
        case 'click_video_play': return 'Clicou para assistir à demonstração';
        case 'video_watch_progress': return `Assistiu até ${m.percent}% do vídeo`;
        case 'click_buy': return m.source === 'hero' ? 'Clicou em "Garantir meu acesso" (topo)' : 'Clicou em "Quero adquirir agora" (fechamento)';
        case 'checkout_started': return 'Abriu o checkout';
        case 'checkout_step':
            if (m.step === 'identification_submitted') return m.wasNewAccount ? 'Criou uma conta nova' : 'Identificou-se com conta existente';
            if (m.step === 'payment_method_selected') return `Escolheu pagar via ${m.method === 'pix' ? 'Pix' : 'cartão'}`;
            if (m.step === 'payment_failed') return 'Pagamento recusado';
            return 'Avançou uma etapa do checkout';
        case 'payment_approved': return 'Pagamento aprovado';
        case 'checkout_abandoned': return `Abandonou o checkout (estava em: ${m.lastStep || '—'})`;
        default: return e.eventType;
    }
}

const RyanAdminAnalyticsTimeline: React.FC<Props> = ({ productId, periodParams, source }) => {
    const [outcome, setOutcome] = useState('all');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState<SessionsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
    const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
    const [loadingTimeline, setLoadingTimeline] = useState(false);
    // Força a lista a recarregar depois de uma exclusão bem-sucedida — nenhum
    // dos outros deps do efeito de fetch muda sozinho nesse momento.
    const [refreshTick, setRefreshTick] = useState(0);

    // Fluxo de exclusão (2 etapas: preview só-leitura, depois confirmação) —
    // deletePreview null = modal fechado. loadingPreview cobre o primeiro
    // fetch (preview); deleting cobre o DELETE de fato.
    const [deletePreview, setDeletePreview] = useState<DeletePreview | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Debounce simples — evita um fetch por tecla digitada na busca de e-mail.
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => { setPage(1); }, [productId, periodParams, source, outcome, search]);

    useEffect(() => {
        if (!productId || !periodParams) return;
        setLoading(true);
        const params = new URLSearchParams({ ...periodParams, source, outcome, page: String(page), pageSize: '25' });
        if (search) params.set('search', search);
        api(`/admin/analytics/${productId}/sessions?${params.toString()}`)
            .then(r => r.ok ? r.json() : null)
            .then(setData)
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, periodParams, source, outcome, search, page, refreshTick]);

    useEffect(() => {
        if (!selectedSessionId) { setTimeline(null); return; }
        setLoadingTimeline(true);
        api(`/admin/analytics/${productId}/sessions/${selectedSessionId}`)
            .then(r => r.ok ? r.json() : null)
            .then(setTimeline)
            .finally(() => setLoadingTimeline(false));
    }, [productId, selectedSessionId]);

    // Abre o modal com o preview (só-leitura, nenhum efeito colateral) — a
    // exclusão de fato só acontece se o admin confirmar dentro do modal.
    const openDeletePreview = () => {
        if (!selectedSessionId) return;
        setDeleteError(null);
        setLoadingPreview(true);
        api(`/admin/analytics/${productId}/sessions/${selectedSessionId}/delete-preview`)
            .then(r => r.ok ? r.json() : Promise.reject())
            .then(setDeletePreview)
            .catch(() => setDeleteError('Não foi possível carregar o preview de exclusão.'))
            .finally(() => setLoadingPreview(false));
    };

    const closeDeleteModal = () => {
        setDeletePreview(null);
        setDeleteError(null);
    };

    const confirmDelete = async () => {
        if (!selectedSessionId) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            const res = await api(`/admin/analytics/${productId}/sessions/${selectedSessionId}`, { method: 'DELETE' });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                setDeleteError(d.message || 'Erro ao excluir.');
                return;
            }
            // Sessão não existe mais — fecha o modal, volta pra lista, e força
            // ela a recarregar (a sessão excluída não pode continuar aparecendo).
            setDeletePreview(null);
            setSelectedSessionId(null);
            setRefreshTick(t => t + 1);
        } catch {
            setDeleteError('Erro de conexão.');
        } finally {
            setDeleting(false);
        }
    };

    if (selectedSessionId) {
        return (
            <div className="rana-timeline-wrap">
                <div className="rana-timeline-topbar">
                    <button type="button" className="rana-back-btn" onClick={() => setSelectedSessionId(null)}>
                        ← Voltar pra lista
                    </button>
                    <button
                        type="button" className="rana-delete-session-btn"
                        onClick={openDeletePreview} disabled={loadingTimeline || !timeline}
                    >
                        Excluir sessão
                    </button>
                </div>
                {loadingTimeline || !timeline ? (
                    <div className="ras-spinner-wrap"><div className="ras-spinner" /></div>
                ) : (
                    <>
                        <div className="rana-timeline-header">
                            <span className="rana-timeline-user">{timeline.userLabel || 'Visitante anônimo'}</span>
                            <span className="rana-timeline-source">{timeline.source === 'referral' ? 'Indicação' : 'Direto'}</span>
                        </div>
                        <div className="rana-timeline">
                            {timeline.events.map((e, i) => (
                                <div key={i} className="rana-timeline-item">
                                    <div className="rana-timeline-dot" />
                                    <div className="rana-timeline-time">{formatTimelineTime(e.timestamp)}</div>
                                    <div className="rana-timeline-desc">{describeEvent(e)}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {(loadingPreview || deletePreview) && (
                    <div className="rana-modal-overlay" onClick={() => !deleting && closeDeleteModal()}>
                        <div className="rana-modal" onClick={e => e.stopPropagation()}>
                            {loadingPreview || !deletePreview ? (
                                <div className="ras-spinner-wrap"><div className="ras-spinner" /></div>
                            ) : (
                                <>
                                    <h3 className="rana-modal-title">Excluir sessão</h3>
                                    <p className="rana-modal-body">
                                        Isso vai apagar <strong>{deletePreview.eventCount} evento{deletePreview.eventCount !== 1 ? 's' : ''}</strong> de
                                        comportamento desta sessão ({formatTimelineTime(deletePreview.firstEventAt)} até {formatTimelineTime(deletePreview.lastEventAt)}).
                                    </p>

                                    {deletePreview.tier === 1 && (
                                        <p className="rana-modal-note rana-modal-note--safe">
                                            Nenhuma tentativa de compra está vinculada a esta sessão — exclusão segura e completa.
                                        </p>
                                    )}

                                    {deletePreview.tier === 2 && deletePreview.accessRequest && (
                                        <p className="rana-modal-note rana-modal-note--warn">
                                            Também tem uma tentativa de compra vinculada ({deletePreview.accessRequest.email},{' '}
                                            {REQUEST_STATUS_LABELS[deletePreview.accessRequest.status] || deletePreview.accessRequest.status}),
                                            que nunca virou venda — será apagada junto.
                                            {deletePreview.accessRequest.couponReserved && (
                                                <> A vaga do cupom <strong>{deletePreview.accessRequest.couponCode}</strong> será liberada.</>
                                            )}
                                        </p>
                                    )}

                                    {deletePreview.tier === 3 && deletePreview.accessRequest && (
                                        <p className="rana-modal-note rana-modal-note--danger">
                                            Esta sessão virou uma <strong>venda aprovada</strong> em {formatDateTime(deletePreview.accessRequest.approvedAt || deletePreview.accessRequest.createdAt)}{' '}
                                            ({deletePreview.accessRequest.email}). Só os eventos de comportamento serão apagados aqui — a venda,
                                            o acesso concedido e uma eventual comissão de embaixador não são tocados. Pra reverter a venda em si,
                                            use <strong>Gestão → Acesso</strong>.
                                        </p>
                                    )}

                                    {deleteError && <p className="rana-modal-error">{deleteError}</p>}

                                    <div className="rana-modal-actions">
                                        <button type="button" className="rana-modal-cancel" onClick={closeDeleteModal} disabled={deleting}>
                                            Cancelar
                                        </button>
                                        <button type="button" className="rana-modal-confirm" onClick={confirmDelete} disabled={deleting}>
                                            {deleting ? 'Excluindo...' : deletePreview.tier === 1
                                                ? 'Excluir eventos'
                                                : deletePreview.tier === 2
                                                    ? 'Excluir eventos e a tentativa de compra'
                                                    : 'Excluir apenas os eventos de comportamento'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="rana-sessions-wrap">
            <div className="rana-sessions-toolbar">
                <div className="rana-pills">
                    {OUTCOMES.map(o => (
                        <button
                            key={o.value} type="button"
                            className={`rana-pill${outcome === o.value ? ' rana-pill--active' : ''}`}
                            onClick={() => setOutcome(o.value)}
                        >{o.label}</button>
                    ))}
                </div>
                <input
                    className="ras-input ras-input--sm" placeholder="Buscar por e-mail..."
                    value={searchInput} onChange={e => setSearchInput(e.target.value)}
                />
            </div>

            {loading || !data ? (
                <div className="ras-spinner-wrap"><div className="ras-spinner" /></div>
            ) : data.sessions.length === 0 ? (
                <div className="rana-empty">Nenhuma sessão encontrada.</div>
            ) : (
                <>
                    <div className="rana-sessions-table-wrap">
                        <table className="rana-sessions-table">
                            <thead>
                                <tr>
                                    <th>Início</th><th>Duração</th><th>Origem</th>
                                    <th>Foi até</th><th>Desfecho</th><th>Conta</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {data.sessions.map(s => (
                                    <tr
                                        key={s.sessionId} className="rana-session-row"
                                        onClick={() => setSelectedSessionId(s.sessionId)}
                                        role="button" tabIndex={0}
                                        onKeyDown={e => { if (e.key === 'Enter') setSelectedSessionId(s.sessionId); }}
                                    >
                                        <td>{formatDateTime(s.firstEventAt)}</td>
                                        <td>{formatDuration(s.durationSeconds)}</td>
                                        <td>{s.source === 'referral' ? 'Indicação' : 'Direto'}</td>
                                        <td>{s.furthestStep.label}</td>
                                        <td><span className={`rana-outcome-badge rana-outcome-badge--${s.outcome}`}>{OUTCOME_LABELS[s.outcome]}</span></td>
                                        <td>{s.userLabel || '—'}</td>
                                        <td className="rana-session-arrow">→</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="rana-pagination">
                        <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Anterior</button>
                        <span>{page} de {Math.max(1, Math.ceil(data.total / data.pageSize))} ({data.total.toLocaleString('pt-BR')} sessões)</span>
                        <button type="button" disabled={page * data.pageSize >= data.total} onClick={() => setPage(p => p + 1)}>Próxima →</button>
                    </div>
                </>
            )}
        </div>
    );
};

export default RyanAdminAnalyticsTimeline;
